/**
 * KPI Target Values card (Admin Setup > Performance > OTIF Settings).
 *
 * Split out of OTIFSettingsTab (500-line rule). CR-6266: each KPI keeps its
 * default target (the single value it always had, saved with the settings
 * form) plus target ranges that replace it between dates.
 */
import { PerformanceFormData } from './types';
import { KpiTargetRulesTable } from './KpiTargetRulesTable';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Info, Target } from 'lucide-react';

interface KpiTargetValuesCardProps {
  formData: PerformanceFormData;
  onInputChange: (field: string, value: string | number | boolean) => void;
}

export function KpiTargetValuesCard({ formData, onInputChange }: KpiTargetValuesCardProps) {
  const showOTIF = formData.delivery_metric_mode === 'both' || formData.delivery_metric_mode === 'otif_only';
  const showOTD = formData.delivery_metric_mode === 'both' || formData.delivery_metric_mode === 'otd_only';
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Target className="h-5 w-5 text-primary" />
          <CardTitle>KPI Target Values</CardTitle>
        </div>
        <CardDescription>
          Set target values for OTIF and OTD. These targets appear as goal lines on dashboard graphs and KPI indicators.
          Add target ranges to change a target over time. A range saves as soon as you save its row; the default
          targets save with Save Changes.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid gap-4 md:grid-cols-2">
          {showOTIF && (
            <div className="space-y-2">
              <Label htmlFor="otif_target" className="flex items-center gap-2">
                <Target className="h-4 w-4 text-green-600" />
                Default OTIF Target
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  id="otif_target"
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={formData.otif_target}
                  onChange={(e) => onInputChange('otif_target', e.target.value)}
                />
                <span>%</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Target percentage shown as goal line on OTIF charts, for any date no OTIF target range covers
              </p>
            </div>
          )}
          {showOTD && (
            <div className="space-y-2">
              <Label htmlFor="otd_target" className="flex items-center gap-2">
                <Target className="h-4 w-4 text-blue-600" />
                Default OTD Target
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  id="otd_target"
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={formData.otd_target}
                  onChange={(e) => onInputChange('otd_target', e.target.value)}
                />
                <span>%</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Target percentage shown as goal line on OTD charts, for any date no OTD target range covers
              </p>
            </div>
          )}
        </div>
        {showOTIF && (
          <div className="mt-6">
            <KpiTargetRulesTable kpi="otif" label="OTIF" unit="%" />
          </div>
        )}
        {showOTD && (
          <div className="mt-6">
            <KpiTargetRulesTable kpi="otd" label="OTD" unit="%" />
          </div>
        )}
        <div className="mt-4 p-3 bg-muted rounded-lg">
          <div className="flex items-center gap-2 text-sm">
            <Info className="h-4 w-4" />
            <span className="font-medium">Target vs. Threshold</span>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Target values set the goal line on charts. Color thresholds below determine the green/yellow/red color coding of actual performance values.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
